import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import PaginationWithState from "components/Pagination/PaginationWithState";
import EAddProject from "./EAddProject";
import useEmailArtwork from "./EmailArtworkContext";
import useInbox from "../InboxTab/InboxContext";
import { truncateString } from "utils/UtilityFuncs";

export default function EArtworkProjects() {
  const dispatch = useDispatch();
  const { selectedProject, setSelectedProject } = useEmailArtwork();
  const { selectedGrantEmail } = useInbox();

  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [projects, setProjects] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [loading, setLoading] = useState(false);

  const onHide = () => {
    setShowAddModal(false);
    setIsUpdateMode(false);
    setFormData(null);
  };

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /email-artworks?page=&limit=)
  const getProjects = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/email-artworks?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
            grantEmail: selectedGrantEmail,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() },
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setProjects(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, searchTerm, pageLimit, filterBy.value],
  );

  // Delete EartworkProjects
  const deleteProjects = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/email-artwork/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        // refresh current page
        await getProjects();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          }),
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    try {
      const { data } = await API.post(
        "/email-artwork-sort-up",
        { eArtworkId: id },
        { headers: authHeader() },
      );
      if (data.success) {
        await getProjects();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Down
  const sortDown = async (id) => {
    try {
      const { data } = await API.post(
        "/email-artwork-sort-down",
        { eArtworkId: id },
        { headers: authHeader() },
      );
      if (data.success) {
        await getProjects();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateData = async (item) => {
    setFormData(item);
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getProjects(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber },
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy]);

  /*   Table functions
   ********************************************* */
  let filters = [
    { value: "name", name: "Data Name" },
    { value: "contactName", name: "Contact Name" },
    { value: "contactNumber", name: "Contact Number" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    // { name: "Art-ID", value: "artworkId" },
    { name: "Project", value: "name" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr
      onClick={(e) => {
        setSelectedProject(item);
      }}
      className={`cursor-pointer ${
        selectedProject?.id === item.id ? "table-active" : ""
      }`}
      key={item.id}
    >
      {/* <td className="align-middle py-1">{item.artworkId}</td> */}
      <td className="align-middle py-1 pe-0 w-full">
        {truncateString(item.name, 35)}
      </td>
      <td className="align-middle py-1 ps-0 pe-0 text-nowrap text-end table-action ">
        <button
          onClick={() => {
            sortUp(item.eArtworkId);
          }}
          disabled={item.sortId === projects?.length} // assuming sort by sortId Desc
          className="btn btn-sm px-1  border-0"
        >
          <i className="mdi mdi-chevron-double-up p-0"></i>
        </button>
        <button
          onClick={() => {
            sortDown(item.eArtworkId);
          }}
          disabled={item.sortId === 1} // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-down"></i>
        </button>
        <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>

        <DeleteModalButton
          btnClass={"btn btn-sm  px-1"}
          deleteFunc={deleteProjects}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="">
      <div className="d-flex justify-content-between align-items-center mt-4">
        <h3>Projects</h3>
        <button
          onClick={() => {
            setIsUpdateMode(false);
            setFormData(null);
            setShowAddModal(true);
          }}
          className="btn btn-sm p-1 py-0 rounded-circle btn-outline-primary flex-center"
        >
          <i className="mdi mdi-plus fs-4"></i>
        </button>
      </div>
      <hr className="mt-0" />
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-2 align-items-center">
        <div className="d-none ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex w-100">
          {/* {buttons()} */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getProjects({ query: searchTerm, page: 1 });
              } else {
                getProjects({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex w-100 justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control w-100"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getProjects({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "72vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={projects}
            disableSorting={true}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/* ******************** Modals ************************* */}
      {/* Add / Update Project Modal */}
      <Modal
        show={showAddModal}
        onHide={() => onHide()}
        centered
        style={{
          zIndex: 9999,
        }}
      >
        <EAddProject
          show={showAddModal}
          hideModal={onHide}
          isUpdateMode={isUpdateMode}
          initialData={formData}
          onSaved={() => {
            // refresh current page after add/update
            getProjects({ page: pageNumber });
          }}
        />
      </Modal>
    </div>
  );
}
